/**
 * Предпросмотр сообщения в стиле клиента Discord: классические эмбеды,
 * вложения, опросы, компоненты и Components V2.
 */
import type {
  DAction,
  DActionRow,
  DAttachment,
  DButton,
  DContainer,
  DEmbed,
  DGallery,
  DSection,
  DSelect,
  DV2Child,
  DV2Top,
  Draft,
} from '../../../src/shared/types.js';
import { isEmbedEmpty } from '../../../src/shared/validate.js';
import { previewUrl } from '../api.js';
import { h, hexColor, icon } from '../dom.js';
import { app } from '../ctx.js';
import { isJumbo, renderMarkdown, type MdCtx, type MdMode } from './markdown.js';
import { calendarTime } from './time.js';

export interface PreviewOptions {
  author: { name: string; avatarUrl: string };
  /** клик по интерактивному элементу */
  onAction?: (action: DAction, label: string) => void;
  ephemeral?: boolean;
  edited?: boolean;
}

export const mdCtx: MdCtx = {
  role: (id) => {
    const r = app.guild?.roles.find((x) => x.id === id);
    return r ? { name: r.everyone ? 'everyone' : r.name, color: r.color } : undefined;
  },
  channel: (id) => {
    const c = app.guild?.channels.find((x) => x.id === id) ?? app.guild?.threads.find((x) => x.id === id);
    return c ? { name: c.name, type: c.type } : undefined;
  },
  user: (id) => (app.guild?.me.id === id ? app.guild.me.username : app.guild?.bot.id === id ? app.guild.bot.username : undefined),
  emojiUrl: (id, animated) => `https://cdn.discordapp.com/emojis/${id}.${animated ? 'gif' : 'webp'}?size=96&quality=lossless`,
};

function md(text: string, mode: MdMode, jumboAllowed = false): HTMLElement {
  const node = renderMarkdown(text, mode, mdCtx);
  if (jumboAllowed && isJumbo(text)) node.classList.add('md-jumbo');
  return node;
}

/** attachment://name и upload:// превращаем в отображаемые ссылки. */
function makeResolver(draft: Draft) {
  const byName = new Map<string, DAttachment>(draft.attachments.map((a) => [a.name, a]));
  return (url: string): string => {
    const v = (url || '').trim();
    if (v.startsWith('attachment://')) {
      const a = byName.get(v.slice('attachment://'.length));
      return a ? previewUrl(a.source) : '';
    }
    return previewUrl(v);
  };
}

const IMAGE_EXT = /\.(png|jpe?g|gif|webp|avif)(\?|$)/i;
const VIDEO_EXT = /\.(mp4|webm|mov|m4v)(\?|$)/i;

function mediaKind(name: string, url: string): 'image' | 'video' | 'file' {
  if (IMAGE_EXT.test(name) || IMAGE_EXT.test(url)) return 'image';
  if (VIDEO_EXT.test(name) || VIDEO_EXT.test(url)) return 'video';
  // ссылки без расширения (например, на CDN с параметрами) считаем картинками
  return /^https?:/.test(url) && !/\.[a-z0-9]{2,4}(\?|$)/i.test(url) ? 'image' : 'file';
}

function img(src: string, cls: string, alt = ''): HTMLElement {
  if (!src) return h('div', { class: `${cls} dc-img-empty` }, icon('image', 22));
  const i = h('img', { class: cls, src, alt, loading: 'lazy', draggable: false, referrerPolicy: 'no-referrer' });
  i.addEventListener('error', () => i.replaceWith(h('div', { class: `${cls} dc-img-broken`, title: 'Не удалось загрузить' }, icon('alert', 20))));
  return i;
}

function media(src: string, name: string, cls: string, alt = ''): HTMLElement {
  if (mediaKind(name, src) === 'video' && src) {
    return h('video', { class: cls, src, controls: true, preload: 'metadata', muted: true });
  }
  return img(src, cls, alt);
}

function spoilerWrap(node: HTMLElement, spoiler: boolean): HTMLElement {
  if (!spoiler) return node;
  const wrap = h('div', { class: 'dc-spoiler-media', onclick: () => wrap.classList.add('revealed') }, node, h('span', { class: 'dc-spoiler-label' }, 'СПОЙЛЕР'));
  return wrap;
}

/* ---------------- эмбеды ---------------- */

function fieldColumns(fields: DEmbed['fields'], hasThumb: boolean): string[] {
  const max = hasThumb ? 2 : 3;
  const cols: string[] = [];
  let i = 0;
  while (i < fields.length) {
    if (!fields[i].inline) {
      cols.push('1 / 13');
      i++;
      continue;
    }
    let j = i;
    while (j < fields.length && fields[j].inline && j - i < max) j++;
    const count = j - i;
    const span = 12 / count;
    for (let k = 0; k < count; k++) cols.push(`${1 + span * k} / ${1 + span * (k + 1)}`);
    i = j;
  }
  return cols;
}

function renderEmbed(e: DEmbed, extraImages: string[], resolve: (u: string) => string): HTMLElement {
  const hasThumb = !!e.thumbnail.trim();
  const grid = h('div', { class: ['dc-embed-grid', hasThumb && 'has-thumb'] });

  if (e.author.name) {
    grid.append(
      h(
        'div',
        { class: 'dc-embed-author' },
        e.author.iconUrl ? img(resolve(e.author.iconUrl), 'dc-embed-author-icon') : null,
        e.author.url
          ? h('a', { class: 'dc-embed-author-name', href: e.author.url, target: '_blank', rel: 'noreferrer' }, e.author.name)
          : h('span', { class: 'dc-embed-author-name' }, e.author.name),
      ),
    );
  }
  if (e.title) {
    const t = md(e.title, 'title');
    grid.append(
      e.url
        ? h('a', { class: 'dc-embed-title is-link', href: e.url, target: '_blank', rel: 'noreferrer' }, t)
        : h('div', { class: 'dc-embed-title' }, t),
    );
  }
  if (e.description) grid.append(h('div', { class: 'dc-embed-desc' }, md(e.description, 'full')));
  if (e.fields.length) {
    const cols = fieldColumns(e.fields, hasThumb);
    grid.append(
      h(
        'div',
        { class: 'dc-embed-fields' },
        e.fields.map((f, i) =>
          h(
            'div',
            { class: 'dc-embed-field', style: { gridColumn: cols[i] } },
            h('div', { class: 'dc-embed-field-name' }, md(f.name, 'title')),
            h('div', { class: 'dc-embed-field-value' }, md(f.value, 'full')),
          ),
        ),
      ),
    );
  }
  const images = [e.image, ...extraImages].filter((x) => x.trim()).map(resolve);
  if (images.length === 1) {
    grid.append(h('div', { class: 'dc-embed-media' }, img(images[0], 'dc-embed-image')));
  } else if (images.length > 1) {
    grid.append(h('div', { class: `dc-embed-gallery n${images.length}` }, images.map((s) => img(s, 'dc-embed-gallery-img'))));
  }
  if (hasThumb) grid.append(img(resolve(e.thumbnail), 'dc-embed-thumb'));
  if (e.footer.text || e.timestamp) {
    const parts: (Node | string)[] = [];
    if (e.footer.text) parts.push(e.footer.text);
    if (e.footer.text && e.timestamp) parts.push(h('span', { class: 'dc-dot' }, '•'));
    if (e.timestamp) parts.push(calendarTime(new Date(e.timestamp)));
    grid.append(
      h(
        'div',
        { class: 'dc-embed-footer' },
        e.footer.iconUrl && e.footer.text ? img(resolve(e.footer.iconUrl), 'dc-embed-footer-icon') : null,
        h('span', { class: 'dc-embed-footer-text' }, parts),
      ),
    );
  }
  const color = e.color === null ? '' : hexColor(e.color);
  return h('article', { class: 'dc-embed', style: color ? { borderLeftColor: color } : undefined }, grid);
}

function renderEmbeds(embeds: DEmbed[], resolve: (u: string) => string): HTMLElement[] {
  // эмбеды с одинаковой ссылкой Discord объединяет в один с галереей до 4 картинок
  const out: HTMLElement[] = [];
  const consumed = new Set<number>();
  embeds.forEach((e, i) => {
    if (consumed.has(i) || isEmbedEmpty(e)) return;
    const extra: string[] = [];
    if (e.url && e.image) {
      for (let j = i + 1; j < embeds.length && extra.length < 3; j++) {
        if (embeds[j].url === e.url && embeds[j].image) {
          extra.push(embeds[j].image);
          consumed.add(j);
        }
      }
    }
    out.push(renderEmbed(e, extra, resolve));
  });
  return out;
}

/* ---------------- компоненты ---------------- */

function emojiNode(value: string, cls = 'dc-emoji'): Node | null {
  const v = value.trim();
  if (!v) return null;
  const m = /^<(a?):([\w~]+):(\d+)>$/.exec(v);
  if (m) return h('img', { class: cls, src: mdCtx.emojiUrl(m[3], m[1] === 'a'), alt: m[2], draggable: false });
  return h('span', { class: `${cls} is-unicode` }, v);
}

function renderButton(b: DButton, o: PreviewOptions): HTMLElement {
  const btn = h(
    'button',
    {
      class: ['dc-btn', `dc-btn-${b.style}`, b.disabled && 'is-disabled'],
      type: 'button',
      title: b.style === 'link' ? b.url : actionTitle(b.action),
      onclick: () => {
        if (b.disabled) return;
        if (b.style === 'link') {
          if (/^https?:\/\//.test(b.url)) window.open(b.url, '_blank', 'noopener');
        } else o.onAction?.(b.action, b.label);
      },
    },
    emojiNode(b.emoji),
    b.label ? h('span', { class: 'dc-btn-label' }, b.label) : null,
    b.style === 'link' ? icon('external', 14, 'dc-btn-ext') : null,
  );
  return btn;
}

function actionTitle(a: DAction): string {
  switch (a.type) {
    case 'template':
      return 'Ответит шаблоном (видно только нажавшему)';
    case 'role':
      return a.mode === 'add' ? 'Выдаст роль' : a.mode === 'remove' ? 'Снимет роль' : 'Выдаст или снимет роль';
    case 'raw':
      return `custom_id: ${a.customId}`;
    default:
      return 'Ничего не делает';
  }
}

function renderSelect(s: DSelect, o: PreviewOptions): HTMLElement {
  const defaults = s.options.filter((x) => x.isDefault);
  const label = defaults.length
    ? h('span', { class: 'dc-select-value' }, defaults.map((d) => d.label).join(', '))
    : h('span', { class: 'dc-select-placeholder' }, s.placeholder || 'Сделайте выбор');
  const menu = h(
    'div',
    { class: 'dc-select-menu' },
    s.options.map((opt) =>
      h(
        'div',
        {
          class: ['dc-select-option', opt.isDefault && 'is-selected'],
          onclick: (ev: MouseEvent) => {
            ev.stopPropagation();
            wrap.classList.remove('open');
            o.onAction?.(opt.action, opt.label);
          },
        },
        emojiNode(opt.emoji, 'dc-select-emoji'),
        h('div', { class: 'dc-select-option-text' }, h('div', { class: 'dc-select-option-label' }, opt.label), opt.description ? h('div', { class: 'dc-select-option-desc' }, opt.description) : null),
      ),
    ),
  );
  const wrap: HTMLElement = h(
    'div',
    { class: ['dc-select', s.disabled && 'is-disabled'] },
    h(
      'div',
      {
        class: 'dc-select-box',
        onclick: () => {
          if (!s.disabled) wrap.classList.toggle('open');
        },
      },
      label,
      icon('chevron', 18, 'dc-select-chevron'),
    ),
    menu,
  );
  return wrap;
}

function renderRow(r: DActionRow, o: PreviewOptions): HTMLElement {
  return h('div', { class: 'dc-row' }, r.items.map((it) => (it.type === 'button' ? renderButton(it, o) : renderSelect(it, o))));
}

/* ---------------- вложения и опрос ---------------- */

function fileCard(name: string, href: string, spoiler = false): HTMLElement {
  return spoilerWrap(
    h(
      'div',
      { class: 'dc-file' },
      h('div', { class: 'dc-file-icon' }, icon('file', 26)),
      h('div', { class: 'dc-file-meta' }, h('a', { class: 'dc-file-name', href: href || undefined, target: '_blank', rel: 'noreferrer' }, name), h('div', { class: 'dc-file-size' }, 'вложение')),
      icon('download', 20, 'dc-file-dl'),
    ),
    spoiler,
  );
}

function renderAttachments(draft: Draft): HTMLElement | null {
  if (!draft.attachments.length) return null;
  const visual = draft.attachments.filter((a) => mediaKind(a.name, a.source) !== 'file');
  const files = draft.attachments.filter((a) => mediaKind(a.name, a.source) === 'file');
  return h(
    'div',
    { class: 'dc-attachments' },
    visual.length
      ? h(
          'div',
          { class: `dc-gallery g${Math.min(visual.length, 10)}` },
          visual.map((a) => spoilerWrap(media(previewUrl(a.source), a.name, 'dc-gallery-item', a.description), a.spoiler)),
        )
      : null,
    files.map((a) => fileCard(a.name, previewUrl(a.source), a.spoiler)),
  );
}

function renderPoll(draft: Draft): HTMLElement | null {
  const p = draft.poll;
  if (!p) return null;
  const hours = p.durationHours;
  const left = hours >= 48 ? `${Math.round(hours / 24)} д.` : `${hours} ч`;
  return h(
    'div',
    { class: 'dc-poll' },
    h('div', { class: 'dc-poll-q' }, p.question || 'Вопрос'),
    h('div', { class: 'dc-poll-hint' }, p.multiselect ? 'Выберите один или несколько ответов' : 'Выберите один ответ'),
    h(
      'div',
      { class: 'dc-poll-answers' },
      p.answers.map((a) =>
        h(
          'label',
          { class: 'dc-poll-answer' },
          emojiNode(a.emoji, 'dc-poll-emoji'),
          h('span', { class: 'dc-poll-text' }, a.text || 'Ответ'),
          h('span', { class: ['dc-poll-check', p.multiselect ? 'is-square' : 'is-round'] }),
        ),
      ),
    ),
    h(
      'div',
      { class: 'dc-poll-footer' },
      h('span', null, `0 голосов`, h('span', { class: 'dc-dot' }, '•'), `Осталось ${left}`),
      h('button', { class: 'dc-btn dc-btn-primary is-disabled', type: 'button' }, 'Голосовать'),
    ),
  );
}

/* ---------------- Components V2 ---------------- */

function galleryLayout(n: number): string {
  return `dc-gallery g${Math.min(Math.max(n, 1), 10)}`;
}

function renderGallery(g: DGallery, resolve: (u: string) => string): HTMLElement {
  return h(
    'div',
    { class: galleryLayout(g.items.length) },
    g.items.map((it) => spoilerWrap(media(resolve(it.url), it.url, 'dc-gallery-item', it.description), it.spoiler)),
  );
}

function renderSection(s: DSection, o: PreviewOptions, resolve: (u: string) => string): HTMLElement {
  const acc = s.accessory;
  return h(
    'div',
    { class: 'dc-section' },
    h('div', { class: 'dc-section-texts' }, s.texts.map((t) => h('div', { class: 'dc-text' }, md(t.content, 'full')))),
    h(
      'div',
      { class: 'dc-section-accessory' },
      acc.type === 'button' ? renderButton(acc, o) : spoilerWrap(img(resolve(acc.url), 'dc-thumbnail', acc.description), acc.spoiler),
    ),
  );
}

function renderV2Child(c: DV2Child, draft: Draft, o: PreviewOptions, resolve: (u: string) => string): HTMLElement {
  switch (c.type) {
    case 'text':
      return h('div', { class: 'dc-text' }, md(c.content, 'full', true));
    case 'section':
      return renderSection(c, o, resolve);
    case 'gallery':
      return renderGallery(c, resolve);
    case 'file': {
      const a = draft.attachments.find((x) => x.name === c.attachment);
      return fileCard(c.attachment || 'файл не выбран', a ? previewUrl(a.source) : '', c.spoiler);
    }
    case 'separator':
      return h('div', { class: ['dc-separator', c.spacing === 2 ? 'is-large' : 'is-small', c.divider && 'has-divider'] });
    case 'row':
      return renderRow(c, o);
  }
}

function renderContainer(c: DContainer, draft: Draft, o: PreviewOptions, resolve: (u: string) => string): HTMLElement {
  const color = c.accent === null ? '' : hexColor(c.accent);
  const box = h(
    'div',
    { class: ['dc-container', color && 'has-accent'], style: color ? ({ '--dc-accent': color } as any) : undefined },
    c.children.map((ch) => renderV2Child(ch, draft, o, resolve)),
  );
  if (!c.spoiler) return box;
  const wrap = h('div', { class: 'dc-spoiler-container', onclick: () => wrap.classList.add('revealed') }, box, h('span', { class: 'dc-spoiler-label' }, 'СПОЙЛЕР'));
  return wrap;
}

function renderV2(draft: Draft, o: PreviewOptions): HTMLElement {
  const resolve = makeResolver(draft);
  return h(
    'div',
    { class: 'dc-v2' },
    draft.v2.map((c: DV2Top) => (c.type === 'container' ? renderContainer(c, draft, o, resolve) : renderV2Child(c, draft, o, resolve))),
  );
}

/* ---------------- сообщение целиком ---------------- */

export function renderMessage(draft: Draft, o: PreviewOptions): HTMLElement {
  const body = h('div', { class: 'dc-body' });
  const now = new Date();
  body.append(
    h(
      'div',
      { class: 'dc-header' },
      h('span', { class: 'dc-name' }, o.author.name || 'Embed Studio'),
      h('span', { class: 'dc-tag' }, icon('check', 10), 'ПРИЛОЖЕНИЕ'),
      h('span', { class: 'dc-time' }, calendarTime(now)),
    ),
  );

  if (draft.mode === 'v2') {
    body.append(renderV2(draft, o));
  } else {
    const resolve = makeResolver(draft);
    if (draft.content) {
      const c = md(draft.content, 'full', true);
      c.classList.add('dc-content');
      if (o.edited) c.append(h('span', { class: 'dc-edited' }, ' (изменено)'));
      body.append(c);
    }
    const embeds = draft.options.suppressEmbeds ? [] : renderEmbeds(draft.embeds, resolve);
    if (embeds.length) body.append(h('div', { class: 'dc-embeds' }, embeds));
    const att = renderAttachments(draft);
    if (att) body.append(att);
    const poll = renderPoll(draft);
    if (poll) body.append(poll);
    if (draft.rows.length) body.append(h('div', { class: 'dc-rows' }, draft.rows.map((r) => renderRow(r, o))));
  }

  if (o.ephemeral) {
    body.append(h('div', { class: 'dc-ephemeral' }, icon('eye', 14), 'Только вы видите это сообщение', h('span', { class: 'dc-link-like' }, 'Скрыть сообщение')));
  }

  return h(
    'div',
    { class: ['dc-msg', o.ephemeral && 'is-ephemeral'] },
    h('img', { class: 'dc-avatar', src: o.author.avatarUrl || 'https://cdn.discordapp.com/embed/avatars/0.png', alt: '', draggable: false }),
    body,
  );
}
